import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { OnboardingInput } from '@freelancer-os/validation';
import { fetchApi } from '../lib/api';
import i18n from '../i18n';

interface OrgSettingsResponse {
  organization: {
    id: number;
    name: string;
    slug: string;
    logoUrl: string | null;
    bankCode: string | null;
    bankAccountNo: string | null;
    bankAccountName: string | null;
  };
  settings: {
    industry: string | null;
    teamSize: string | null;
    preferredChannel: string | null;
    defaultCurrency: string | null;
    taxCategory: string | null;
    aiPersonalization: unknown | null;
  };
}

export function useOrgSettings() {
  return useQuery<OrgSettingsResponse>({
    queryKey: ['org', 'settings'],
    queryFn: () => fetchApi<OrgSettingsResponse>('/org/settings'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: OnboardingInput) =>
      fetchApi<{ message: string }>('/auth/onboarding', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
      toast.success(i18n.t('preferences.toastUpdated', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('preferences.toastError', { ns: 'settings' }));
    },
  });
}
